import type { Ref } from 'vue'
import type { IconDetail, MenuItemDetail } from '~~/shared/types/menu'
import { childrenOf, isAdditive, isParent, isSub, parentOf } from '~/shared/allergenHierarchy'

const FILTER_KEY = Symbol('allergenFilter')
const STORAGE_KEY = 'allergenFilter'

interface StoredFilter {
  excludedAllergens: string[]
  activeTraits: string[]
}

function saveToStorage(excludedAllergens: Set<string>, activeTraits: Set<string>) {
  if (!import.meta.client) return
  try {
    const data: StoredFilter = {
      excludedAllergens: [...excludedAllergens],
      activeTraits: [...activeTraits],
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }
  catch (error) {
    console.error('Error saving allergen filter to storage:', error)
  }
}

function loadFromStorage(): StoredFilter | null {
  if (!import.meta.client) return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw)
    if (!Array.isArray(data.excludedAllergens) || !Array.isArray(data.activeTraits)) return null
    return data as StoredFilter
  }
  catch {
    return null
  }
}

type AllergenGroupState = 'checked' | 'indeterminate' | 'unchecked'

interface AllergenFilter {
  initialized: Ref<boolean>
  isFilterOpen: Ref<boolean>
  excludedAllergens: Ref<Set<string>>
  activeTraits: Ref<Set<string>>
  pendingExcludedAllergens: Ref<Set<string>>
  pendingTraits: Ref<Set<string>>
  allergens: ComputedRef<IconDetail[]>
  additives: ComputedRef<IconDetail[]>
  nonAdditiveAllergens: ComputedRef<IconDetail[]>
  traits: ComputedRef<IconDetail[]>
  isItemVisible: (item: MenuItemDetail) => boolean
  toggleAllergen: (id: string) => void
  toggleTrait: (id: string) => void
  allergenGroupState: (parentId: string) => AllergenGroupState
  selectAllAllergens: () => void
  deselectAllAllergens: () => void
  selectAllTraits: () => void
  deselectAllTraits: () => void
  resetFilter: () => void
  openFilter: () => void
  applyFilter: () => void
  discardFilter: () => void
}

export function provideAllergenFilter(allIcons: Ref<IconDetail[]>) {
  const initialized = ref(false)
  const isFilterOpen = ref(false)
  const excludedAllergens = ref(new Set<string>())
  const activeTraits = ref(new Set<string>())
  const pendingExcludedAllergens = ref(new Set<string>())
  const pendingTraits = ref(new Set<string>())

  const allergens = computed(() => allIcons.value.filter(i => !i.isTrait))
  // Additives live in their own filter tab; the allergen tab and its
  // select-all operate on everything else.
  const additives = computed(() => allergens.value.filter(i => isAdditive(i.id)))
  const nonAdditiveAllergens = computed(() => allergens.value.filter(i => !isAdditive(i.id)))
  const traits = computed(() => allIcons.value.filter(i => i.isTrait))
  const traitIds = computed(() => new Set(traits.value.map(i => i.id)))
  const allergenIds = computed(() => new Set(allergens.value.map(i => i.id)))

  /** Sub ids of a parent that are actually present in this menu. */
  function presentChildIds(parentId: string): string[] {
    return childrenOf(parentId).filter(id => allergenIds.value.has(id))
  }

  watch(allIcons, (icons) => {
    if (!import.meta.client) return
    if (icons.length > 0 && !initialized.value) {
      const availableAllergenIds = new Set(icons.filter(i => !i.isTrait).map(i => i.id))
      const availableTraitIds = new Set(icons.filter(i => i.isTrait).map(i => i.id))
      const stored = loadFromStorage()

      if (stored) {
        excludedAllergens.value = new Set(stored.excludedAllergens.filter(id => availableAllergenIds.has(id)))
        activeTraits.value = new Set(stored.activeTraits.filter(id => availableTraitIds.has(id)))
      }
      else {
        excludedAllergens.value = new Set<string>()
        activeTraits.value = new Set<string>()
      }
      initialized.value = true
    }
  }, { immediate: true, flush: 'sync' })

  function isItemVisible(item: MenuItemDetail): boolean {
    if (!initialized.value) return true

    const hasActiveTraits = activeTraits.value.size > 0

    // If traits are active: item MUST have at least one active trait
    if (hasActiveTraits) {
      if (!item.icons?.length) return false
      const hasMatchingTrait = item.icons.some(icon => activeTraits.value.has(icon))
      if (!hasMatchingTrait) return false
    }
    else {
      // If no traits are active: items without icons are visible
      if (!item.icons?.length) return true
    }

    // Allergen check: hide item if it contains ANY excluded allergen
    const itemAllergens = item.icons.filter(icon => !traitIds.value.has(icon))
    return !itemAllergens.some(icon => excludedAllergens.value.has(icon))
  }

  function toggleAllergen(id: string) {
    const next = new Set(pendingExcludedAllergens.value)
    const kids = presentChildIds(id)

    if (isParent(id) && kids.length > 0) {
      // Parent acts as (de)select-all for its present sub-allergens. Base the
      // state on the subs so it also works if the generic parent icon happens
      // to be absent from this menu.
      const allOn = kids.every(k => next.has(k))
      if (allOn) {
        kids.forEach(k => next.delete(k))
        next.delete(id)
      }
      else {
        kids.forEach(k => next.add(k))
        if (allergenIds.value.has(id)) next.add(id)
      }
    }
    else if (isSub(id)) {
      if (next.has(id)) next.delete(id)
      else next.add(id)
      // The generic parent only counts as an exclusion when ALL its subs are on
      // (and only when the parent actually exists in this menu).
      const parentId = parentOf(id)!
      const siblings = presentChildIds(parentId)
      if (siblings.length > 0 && siblings.every(k => next.has(k)) && allergenIds.value.has(parentId)) next.add(parentId)
      else next.delete(parentId)
    }
    else {
      // Plain allergen (incl. a parent with no sub-allergens in this menu).
      if (next.has(id)) next.delete(id)
      else next.add(id)
    }

    pendingExcludedAllergens.value = next
  }

  function toggleTrait(id: string) {
    const next = new Set(pendingTraits.value)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    pendingTraits.value = next
  }

  /** Tri-state of a parent checkbox, derived from its present subs. */
  function allergenGroupState(parentId: string): AllergenGroupState {
    const kids = presentChildIds(parentId)
    const pending = pendingExcludedAllergens.value
    if (kids.length === 0) return pending.has(parentId) ? 'checked' : 'unchecked'
    const on = kids.filter(k => pending.has(k)).length
    if (on === 0) return 'unchecked'
    if (on === kids.length) return 'checked'
    return 'indeterminate'
  }

  function selectAllAllergens() {
    // Scoped to the allergen tab: leave any additive selection untouched.
    const next = new Set(pendingExcludedAllergens.value)
    nonAdditiveAllergens.value.forEach(i => next.add(i.id))
    pendingExcludedAllergens.value = next
  }

  function deselectAllAllergens() {
    const next = new Set(pendingExcludedAllergens.value)
    nonAdditiveAllergens.value.forEach(i => next.delete(i.id))
    pendingExcludedAllergens.value = next
  }

  function selectAllTraits() {
    pendingTraits.value = new Set(traits.value.map(i => i.id))
  }

  function deselectAllTraits() {
    pendingTraits.value = new Set()
  }

  function resetFilter() {
    pendingExcludedAllergens.value = new Set()
    pendingTraits.value = new Set()
  }

  function openFilter() {
    pendingExcludedAllergens.value = new Set(excludedAllergens.value)
    pendingTraits.value = new Set(activeTraits.value)
    isFilterOpen.value = true
  }

  function applyFilter() {
    excludedAllergens.value = new Set(pendingExcludedAllergens.value)
    activeTraits.value = new Set(pendingTraits.value)
    initialized.value = true
    isFilterOpen.value = false
    saveToStorage(excludedAllergens.value, activeTraits.value)
  }

  function discardFilter() {
    isFilterOpen.value = false
  }

  const filter: AllergenFilter = {
    initialized,
    isFilterOpen,
    excludedAllergens,
    activeTraits,
    pendingExcludedAllergens,
    pendingTraits,
    allergens,
    additives,
    nonAdditiveAllergens,
    traits,
    isItemVisible,
    toggleAllergen,
    toggleTrait,
    allergenGroupState,
    selectAllAllergens,
    deselectAllAllergens,
    selectAllTraits,
    deselectAllTraits,
    resetFilter,
    openFilter,
    applyFilter,
    discardFilter,
  }

  provide<AllergenFilter>(FILTER_KEY, filter)

  return filter
}

export function useAllergenFilter() {
  const filter = inject<AllergenFilter | undefined>(FILTER_KEY)
  if (!filter) throw new Error('useAllergenFilter must be called inside a provideAllergenFilter context')
  return filter
}
